import { getFactoryEditScrollOffsetPx } from './factory-edit-scroll.util';
import {
  listFactorySections,
  type FactorySection,
} from './factory-section-status.util';

/**
 * Observes factory edit section anchors and reports the topmost visible section.
 *
 * @param options Configuration for scroll-spy behavior.
 * @param options.onActive Called when a new section becomes active.
 * @param options.shouldSkip When true, scroll-spy updates are ignored (e.g. during programmatic scroll).
 * @returns Disconnect function (no-op when observer cannot be created).
 */
export function createFactoryEditSectionObserver(options: {
  onActive: (section: FactorySection) => void;
  shouldSkip: () => boolean;
}): () => void {
  if (typeof IntersectionObserver === 'undefined') {
    return () => undefined;
  }

  const sections = listFactorySections();
  const elements = sections
    .map((id) => document.getElementById(`factory-edit-section-${id}`))
    .filter((el): el is HTMLElement => el !== null);

  if (elements.length === 0) {
    return () => undefined;
  }

  const scrollOffsetPx = getFactoryEditScrollOffsetPx();
  const anchorPrefix = 'factory-edit-section-';

  const observer = new IntersectionObserver(
    (entries) => {
      const visible = entries
        .filter((entry) => entry.isIntersecting)
        .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
      const first = visible[0]?.target;
      if (!(first instanceof HTMLElement)) {
        return;
      }
      const id = first.id.replace(anchorPrefix, '');
      const match = sections.find((section) => section === id);
      if (match && !options.shouldSkip()) {
        options.onActive(match);
      }
    },
    {
      rootMargin: `-${scrollOffsetPx}px 0px -60% 0px`,
      threshold: 0,
    }
  );

  for (const element of elements) {
    observer.observe(element);
  }

  return () => observer.disconnect();
}

/** Wires scroll-spy for factory edit sections using store callbacks. */
export function setupFactoryEditSectionScrollSpy(store: {
  isAutoScrolling: () => boolean;
  setActiveSection: (section: FactorySection) => void;
}): () => void {
  return createFactoryEditSectionObserver({
    onActive: (section) => store.setActiveSection(section),
    shouldSkip: () => store.isAutoScrolling(),
  });
}
